import styled, { css, keyframes } from 'styled-components';
import {
  ctaButtonOverrideCss,
  darkCheckboxCss,
  darkFieldsCss,
} from 'v3/components/FlowScreen/surfaces';
import { Col as ColAnt } from 'antd';
import {
  Description as SignatureDescriptionText,
  Wrapper as Signature,
} from 'v3/components/Signature/styles';
import {
  Container as MinimalBanner,
  Text,
  Description,
} from 'v3/components/MinimalBanner/styles';
import { SpinnerIos } from '@styled-icons/fluentui-system-regular/SpinnerIos';
import {
  CheckWrapper as PaymentMethodCheck,
  Wrapper as PaymentMethodButton,
} from 'components/PaymentMethod/styles';
import { Wrapper as PaymentBox } from 'v3/components/PaymentBox/styles';

/* No mobile override of the card here. There used to be one repainting SidebarBox
   light and stripping its padding — a leftover from the light design that
   out-specified the dark card and left mobile looking like the old screen.
   PayCardShell owns the surface and its own responsive padding at every width. */
export const Wrapper = styled.div`
  width: 100%;
  /* Same column width as Getting Started (720). With the aside removed the Col
     became 24/24 and the card would otherwise fill the container. */
  max-width: 720px;
  margin: 0 auto;
  display: flex;
  flex-wrap: wrap;
`;


/* CTA outside the card, right-aligned — same pattern as Getting Started's CtaRow.
   It stays empty until the payment forms portal the button in. */
export const CtaSlot = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 24px;

  &:empty {
    display: none;
  }

  /* The flow's one CTA treatment, shared with steps 0 and 1.

     The shared block forces the text colour too, which settles a contrast bug here: the
     ACH button switches to the info and success statuses while processing, and those
     modifiers set a colour that the old override left alone. */
  > button {
    min-width: 272px;
    display: flex;
    align-items: center;
    justify-content: center;

    ${ctaButtonOverrideCss}
  }
`;

/* Initials progress under the contract. Filled in the flow's purple like a selected
   chip, so it reads as live progress rather than a warning. It was a light lavender
   pill carrying dark ink, which the dark-mode sweep left as white on lavender —
   the same colour pairing as no badge at all. */
export const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  margin-top: 12px;
  border-radius: 12px;
  border: 1px solid rgba(89, 104, 219, 0.45);
  padding: 5px 12px;
  font-family: 'Inter', sans-serif;
  font-size: 1.4rem;
  font-weight: 500;
  color: #ffffff;
  background-color: rgba(89, 104, 219, 0.18);
  line-height: 1.6rem;
`;

export const Paragraph = styled.p`
  ${({ theme }) => css`
    font-size: 1.5rem;
    color: #e5e5e5;
    line-height: 2rem;
    margin: 2rem 0;
  `}
`;

export const TitleCol = styled(ColAnt)`
  @media (max-width: 1200px) {
    margin: 0;
  }
`;

export const Col = styled(ColAnt)`
  @media (max-width: 992px) {
    margin: 0;
  }
`;

export const OfferCol = styled(ColAnt)`
  position: sticky;
  top: 24px;
  align-self: start;

  @media (max-width: 992px) {
    position: static;
  }
`;

export const TitleSection = styled.section`
  margin-bottom: 40px;
`;

/* The two aside cards were flush: there was only a margin on mobile. */
export const OfferSection = styled.div`
  margin-bottom: 16px;

  @media (max-width: 992px) {
    margin-bottom: 32px;
  }
`;

/* An eyebrow, not a title: the summary label is small and uppercase, inside the
   block. As a 2.4rem h3 it competed with the page title, which is the subject of
   the screen. */
export const OfferTitle = styled.h3`
  font-family: 'Inter', sans-serif;
  font-size: 1.2rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.55);
  margin-bottom: 10px;
`;

export const Title = styled.h2`
  ${({ theme }) => css`
    font-size: 3.2rem;
    line-height: 3.8rem;
    font-weight: 600;
    color: #e5e5e5;
    margin-bottom: 32px;

    @media (max-width: 992px) {
      font-size: 2.4rem;
      line-height: 1.23;
      margin-bottom: 24px;
    }
  `}
`;

export const SectionTitle = styled.h3`
  ${({ theme }) => css`
    font-family: 'Inter', sans-serif;
    font-size: 2rem;
    font-weight: 600;
    /* Full ink: in grey the label read as disabled next to the number. */
    color: #ffffff;
    margin-bottom: 24px;

    /* Section numbering: the number in full tone against the label in a
       secondary tone. */
    .step {
      color: #b3abe8;
    }

    @media (max-width: 992px) {
      margin-bottom: 16px;
    }
  `}
`;

export const ContractAgreementSection = styled.div`
  margin-bottom: 32px;
`;

export const SignatureSection = styled.div`
  margin-bottom: 32px;

  @media (max-width: 992px) {
    margin-bottom: 0;

    ${Signature} {
      border-bottom: 0;
      padding-bottom: 0;
    }
  }
`;

export const SignatureDescription = styled.p`
  ${({ theme }) => css`
    font-size: 1.2rem;
    color: #e5e5e5;
    line-height: 1.6;
  `}
`;

/* No box of its own on mobile. It used to draw a bordered, padded panel inside a card that
   is already bordered and padded — two nested rounded outlines, and 16px of horizontal
   padding on top of the card's own, on the narrowest screen there is. */
export const PaymentMethodSection = styled.div`
  margin-bottom: 32px;
`;

export const LearnPlaid = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 24px;

  a {
    margin-left: 20px;
  }
`;

export const PlaidName = styled.p`
  ${({ theme }) => css`
    font-size: 2rem;
    font-weight: 500;
    color: #ffffff;
    line-height: 1.4;
    margin-right: 8px;
  `}
`;

export const PlaidDescription = styled.p`
  ${({ theme }) => css`
    font-size: 1.8rem;
    color: #e5e5e5;
    line-height: 1.6;
    margin-bottom: 20px;

    @media (max-width: 992px) {
      font-size: 1.6rem;
    }
  `}
`;

export const PaymentDetails = styled.div``;

export const PaymentItem = styled.div``;

export const PaymentDetailTitle = styled.h4`
  ${({ theme }) => css`
    font-size: 1.6rem;
    font-weight: 400;
    color: rgba(255, 255, 255, 0.55);
    line-height: 1.6;
    margin-bottom: 4px;

    > strong {
      font-weight: 500;
      color: #e5e5e5;
    }
  `}
`;

export const PaymentDetailDescription = styled.p`
  ${({ theme }) => css`
    font-size: 1.6rem;
    font-weight: 500;
    color: #e5e5e5;
    line-height: 1.6;
    margin-bottom: 24px;

    > span {
      color: ${theme.colors.brandPrimary70};
    }

    > div {
      display: inline;
      font-size: 1.4rem;
      font-weight: 400;
      color: rgba(255, 255, 255, 0.55);

      @media (max-width: 576px) {
        display: block;
      }
    }
  `}
`;

export const PaymentDetailFirstDescription = styled.p`
  ${({ theme }) => css`
    display: inline;
    font-size: 1.4rem;
    font-weight: 400;
    color: rgba(255, 255, 255, 0.55);
  `}
`;

export const PaymentDetailSpecs = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: 16px;
  margin: 20px 0 32px 0;

  @media (max-width: 610px) {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 45px;
    row-gap: 16px;
  }
`;

export const PaymentDetailSpecsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1.5fr 1fr;
  align-items: center;
  gap: 16px;
  margin: 20px 0 32px 0;

  @media (max-width: 1200px) {
    grid-template-columns: 1fr 1fr;
  }
`;

export const PaymentObs = styled.span`
  ${({ theme }) => css`
    font-size: 1.4rem;
    font-style: italic;
    color: rgba(255, 255, 255, 0.72);
    line-height: 1.6;
    display: block;
    margin-bottom: 32px;
  `}
`;

export const Checkboxes = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 32px;

  a {
    font-size: 1.6rem;
    line-height: 1;
  }
`;

export const FieldWrapper = styled.div``;

export const ButtonSection = styled.div`
  button {
    display: flex;
    justify-content: center;
    min-width: 180px;
  }
`;

export const ErrorMessage = styled.div`
  ${() => css`
    display: flex;
    align-items: center;
    gap: 14px;

    p {
      font-size: 1.4rem;
      line-height: 1.6rem;
      /* Not theme.colors.statusDanger30: this screen renders inside FlowScreen's
         dark surface, where the light theme's red falls to ~3.1:1 and fails AA.
         This is the most saturated red that still clears 4.5:1 there (5.11:1) —
         FlowScreen's own #ff9a9a passes more comfortably but reads pink. */
      color: #ff6b6b;
      font-weight: 500;
    }
  `}
`;

export const Buttons = styled.div`
  ${({ theme }) => css`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    margin-bottom: 24px;

    > a {
      color: ${theme.colors.brandPrimary70};
      border-bottom-color: ${theme.colors.brandPrimary70};
    }

    > button {
      min-width: 272px;

      display: flex;
      align-items: center;
      justify-content: center;
    }

    @media (max-width: 576px) {
      flex-direction: column;
      align-items: center;

      > a {
        order: 2;
      }

      > button {
        display: flex;
        justify-content: center;
        width: 100%;
        order: 1;
      }
    }
  `}
`;

export const EncryptContent = styled.div`
  ${({ theme }) => css`
    border-top: 1px solid ${theme.colors.neutralSilver20};
    border-bottom: 1px solid ${theme.colors.neutralSilver20};
    padding: 20px 0;
    margin-bottom: 24px;
    display: flex;
    justify-content: center;

    @media (max-width: 992px) {
      margin-top: 16px;
    }
  `}
`;

export const LockText = styled.span`
  ${({ theme }) => css`
    font-size: 1.2rem;
    font-style: italic;
    color: ${theme.colors.neutralGray60};
    line-height: 1.6;
  `}
`;

export const PromotionalModalContent = styled.div`
  ${({ theme }) => css`
    p {
      font-size: 1.4rem;
      line-height: 2.2rem;
      margin-bottom: 12px;

      color: #e5e5e5;

      strong {
        font-weight: 700;
      }

      @media (max-width: 500px) {
        font-size: 1.2rem;
        line-height: 1.8rem;
      }
    }

    > div {
      display: flex;
      gap: 4px;
      font-size: 1.2rem;
      line-height: 1.8rem;
      color: ${theme.colors.brandPrimary70};
      font-weight: 500;

      a:hover {
        color: ${theme.colors.brandPrimary70};
      }
    }
  `}
`;

export const DesktopOnly = styled.div`
  display: block;

  @media (max-width: 992px) {
    display: none;
  }
`;

export const MobileOnly = styled.div`
  display: none;

  @media (max-width: 992px) {
    display: block;
  }
`;

export const PaymentMethodChildren = styled.div`
  ${({ theme }) => css`
    margin-top: 24px;

    ${MinimalBanner} {
      background-color: ${theme.colors.neutralSilver5};
      border: 0;
    }

    ${Text} {
      font-size: 1.8rem;
      margin-bottom: 12px;
    }

    ${Description} {
      p {
        font-size: 1.6rem;
        line-height: 1.4;
      }
    }

    @media (max-width: 992px) {
      /* Bottom only: the side padding was indenting this against the card that already
         provides it. */
      padding: 0 0 16px;

      ${Text} {
        font-size: 1.4rem;
        margin-bottom: 4px;
      }

      ${Description} {
        p {
          font-size: 1.2rem;
          line-height: 1.4;
        }
      }
    }
  `}
`;

const spin = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

export const ProcessingIcon = styled(SpinnerIos)`
  animation: 2s linear ${spin} infinite;
  margin-left: 8px;
`;

export const PaymentMethodSelection = styled.div`
  display: flex;
  gap: 24px;
  position: relative;

  ${PaymentMethodButton} {
    width: 200px;
    cursor: pointer;
  }

  /* Mobile: stack the two cards full-width instead of squeezing them side by
     side. Desktop layout unchanged. */
  @media (max-width: 480px) {
    flex-direction: column;
    /* 24px reads as a gap between sections once the two are stacked rather than paired. */
    gap: 12px;

    ${PaymentMethodButton} {
      width: 100%;
    }
  }
`;

export const ChoosePaymentMethodSection = styled.div`
  margin-bottom: 24px;
`;

export const ButtonWrapper = styled.div`
  margin-top: 24px;
`;

export const ChoosePaymentMethodWrapper = styled.div`
  margin-top: 24px;
  position: relative;
`;

export const CardElementWrapper = styled.div`
  max-width: 800px;
  width: 100%;
  border: 1.5px solid rgba(255, 255, 255, 0.14);
  border-radius: 8px;
  padding: 0 7px;
  /* Matches the flow's dark field. Stripe renders the inputs in its own iframe, so
     the text colour inside is set through the CardElement style option, not here. */
  background-color: #2b2a3a;

  &:focus-within {
    border-color: #5968db;
    box-shadow: 0 0 0 3px rgba(89, 104, 219, 0.4);
  }
`;

export const CCSubmitButton = styled.button`
  ${({ theme }) => css`
    margin-top: 24px;

    padding: 8px 16px;
    font-size: 1.6rem;
    background-color: ${theme.colors.brandPrimary70};
    border: none;
    color: ${theme.colors.globalWhite100};

    &:hover {
      background-color: ${theme.colors.purple80};
      color: ${theme.colors.globalWhite100};
    }

    &:active,
    &:focus {
      outline: 2px solid ${theme.colors.brandPrimary30};
      outline-offset: 1px;
    }

    border-radius: 4px;
    font-weight: 500;
    line-height: 1.6;
    display: inline-flex;
    align-items: center;
    text-decoration: none;
    cursor: pointer;

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    // > span {
    //   margin-right: 8px;
    // }
  `}
`;

// --- Light DS "Pay" reskin (portado do design do site) ---

export const PayEyebrow = styled.p`
  font-family: 'Inter', sans-serif;
  font-size: 1.4rem;
  font-weight: 600;
  line-height: 1.5;
  margin: 0 0 8px 0;

  .phase {
    color: #b3abe8;
  }

  .rest {
    color: rgba(255, 255, 255, 0.55);
  }
`;

/* The design system's heading-xl scale (34px), same as the offer title. */
export const PayTitle = styled.h1`
  font-family: 'Inter', sans-serif;
  font-size: 3.4rem;
  font-weight: 600;
  line-height: 1.18;
  letter-spacing: -0.018em;
  color: #ffffff;
  margin: 0 0 8px 0;

  @media (max-width: 576px) {
    font-size: 2.8rem;
  }
`;

export const PaySubtitle = styled.p`
  font-family: 'Inter', sans-serif;
  font-size: 1.6rem;
  font-weight: 400;
  line-height: 1.5;
  color: #bebebe;
  margin: 0 0 32px 0;
`;

/* Dark card in the main column, matching the other steps. Frame only — the
   contract document inside keeps its own styling, because it is a document and
   has to read like one. */
export const PayCardShell = styled.div`
  > aside {
    font-family: 'Inter', sans-serif;
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 16px;
    background: rgba(255, 255, 255, 0.08);
    backdrop-filter: blur(12px);
    -webkit-backdrop-filter: blur(12px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.25);
    padding: 28px 36px;
    color: #e5e5e5;

    /* Narrower sides than top/bottom: the overlay already contributes 16px each side, so
       the card's own 24px was leaving a ~263px column on a 375px phone. */
    @media (max-width: 576px) {
      padding: 24px 16px;
    }
  }

  ${darkFieldsCss}
  ${darkCheckboxCss}

  /* Signature, PaymentBox and PaymentMethod are shared with ChangePaymentMethod and
     the v2 app, so each is themed here by descent rather than modified. */

  /* Signature: the panel takes the dark card, but the pad keeps a light surface.
     The signature is drawn and adopted in black ink, so it has to sit on paper —
     the same reason the contract iframe stays light. */
  ${Signature} {
    background: transparent;
    border-bottom-color: rgba(255, 255, 255, 0.12);

    .ant-tabs-nav::before {
      border-bottom-color: rgba(255, 255, 255, 0.12);
    }

    .ant-tabs-tab .ant-tabs-tab-btn {
      color: rgba(255, 255, 255, 0.72);
    }

    .ant-tabs-tab-active .ant-tabs-tab-btn {
      color: #ffffff;
    }

    .ant-tabs-ink-bar {
      background: #5968db;
    }

    ${SignatureDescriptionText} {
      color: rgba(255, 255, 255, 0.55);
    }
  }

  /* The payment summary box. */
  ${PaymentBox} {
    background: rgba(0, 0, 0, 0.22);
    border-color: rgba(255, 255, 255, 0.1);

    p,
    span,
    strong,
    h1,
    h2,
    h3,
    h4 {
      color: #e5e5e5;
    }

    div {
      border-color: rgba(255, 255, 255, 0.1);
    }
  }

  /* The two payment-option cards. Selection is a styled-components prop, so there
     is no class to match on — the check mark is rendered only for the selected one,
     which makes it the hook. */
  ${PaymentMethodButton} {
    background-color: rgba(255, 255, 255, 0.04);
    border-color: rgba(255, 255, 255, 0.12);

    p,
    span,
    h3,
    h4 {
      color: #e5e5e5;
    }
  }

  ${PaymentMethodButton}:has(${PaymentMethodCheck}) {
    background-color: rgba(89, 104, 219, 0.18);
    border-color: #5968db;
  }

  /* The flow's CTA purple, flat rather than the shared variant's gradient. */
  button[type='submit'] {
    background: #5968db !important;
    border-color: #5968db !important;
  }

  button[type='submit']:hover:not(:disabled) {
    background: #4a58c4 !important;
    border-color: #4a58c4 !important;
  }
`;

/* The unsupported-brand notice under the card field.
   Its own styled <p> rather than a <p> inside ErrorMessage: global.scss declares a
   bare `p { color: #797979 }`, and keeping the colour on this element's own class
   means no descendant-selector specificity race decides whether it renders red.
   FlowScreen's dark panel: most saturated red still clearing 4.5:1 there (5.11:1). */
export const UnsupportedBrandMessage = styled.p`
  margin-top: 12px;
  font-size: 1.4rem;
  line-height: 1.6rem;
  font-weight: 500;
  color: #ff6b6b !important;
`;
